import { useRef, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { isAccessTokenExpired, getRefreshToken, setAuthUser } from './auth';
import { REACT_APP_API_BASE_URL } from './constant';
import { logout } from './auth';


const useAxios = () => {
  const access_token = Cookies.get('access_token');
  const refresh_token = Cookies.get('refresh_token');

  // Create the Axios instance ONCE
  const axiosInstanceRef = useRef(
    axios.create({
      baseURL: REACT_APP_API_BASE_URL,
      // timeout: 5000, // ms
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
  );

  // Update Authorization header and interceptors when tokens change
  useEffect(() => {
    const instance = axiosInstanceRef.current;
    instance.defaults.headers.Authorization = `Bearer ${access_token}`;

    // --- REQUEST INTERCEPTOR ---
    const reqInterceptor = instance.interceptors.request.use(
      async (req) => {
        if (!isAccessTokenExpired(access_token)) {
          return req;
        }
        try {
          const response = await getRefreshToken(refresh_token);
          setAuthUser(response.access, response.refresh);
          req.headers.Authorization = `Bearer ${response.access}`;
          return req;
        } catch (error) {
          Cookies.remove('access_token');
          Cookies.remove('refresh_token');
          logout(); // Clear user session
          window.location.href = '/login';
          return Promise.reject(error);
        }
      },
      (error) => Promise.reject(error)
    );

    // --- RESPONSE INTERCEPTOR ---
    const resInterceptor = instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const response = await getRefreshToken(refresh_token);
            setAuthUser(response.access, response.refresh);
            originalRequest.headers.Authorization = `Bearer ${response.access}`;
            return instance(originalRequest);
          } catch (refreshError) {
            Cookies.remove('access_token');
            Cookies.remove('refresh_token');
            logout(); // Clear user session
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup: remove interceptors on unmount or token change
    return () => {
      instance.interceptors.request.eject(reqInterceptor);
      instance.interceptors.response.eject(resInterceptor);
    };
  }, [access_token, refresh_token]);

  return axiosInstanceRef.current;
};

export default useAxios;